import React, { useEffect } from 'react';

import { useDraggablePanel } from '../utils/panelDragUtils';

interface OverlayPanelProps {
  isOpen: boolean;
  title?: string;
  children: React.ReactNode;
  onClose: () => void;
  closeOnEscape?: boolean;
  closeOnBackdrop?: boolean;
  draggable?: boolean;
  storageKey?: string;
  defaultPosition?: { x: number; y: number };
  zIndex?: number;
  width?: number | string;
  clampToViewport?: boolean;
  hideCloseButton?: boolean;
}

export const OverlayPanel: React.FC<OverlayPanelProps> = ({
  isOpen,
  title,
  children,
  onClose,
  closeOnEscape = true,
  closeOnBackdrop = true,
  draggable = false,
  storageKey,
  defaultPosition,
  zIndex = 3000,
  width,
  clampToViewport = true,
  hideCloseButton = false,
}) => {
  const { handleHeaderMouseDown, panelRef, style } = useDraggablePanel(
    defaultPosition?.x ?? 0,
    defaultPosition?.y ?? 0,
    {
      storageKey,
      clampToViewport,
    }
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!closeOnEscape) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeOnEscape, isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="panel-overlay"
      style={{
        zIndex,
      }}
      onClick={() => {
        if (closeOnBackdrop) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        style={{
          position: 'fixed',
          ...(draggable ? style : undefined),
          width,
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {title && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: '10px 12px',
              background: '#0d1117',
              border: '1px solid #21262d',
              borderBottom: 'none',
              boxShadow: '0 0 0 1px #161b22',
              userSelect: 'none',
              cursor: draggable ? 'grab' : 'default',
              fontFamily: 'var(--font-mono)',
            }}
            onMouseDown={(e) => {
              if (!draggable) return;
              handleHeaderMouseDown(e);
            }}
          >
            <div style={{
              color: '#e5e7eb',
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'uppercase',
              fontFamily: 'var(--font-lookout)',
            }}>
              {title}
            </div>
            {!hideCloseButton && (
              <button
                type="button"
                style={{
                  background: 'transparent',
                  border: '1px solid #21262d',
                  color: '#9ca3af',
                  padding: '2px 8px',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-mono)',
                }}
                onClick={onClose}
              >
                [X]
              </button>
            )}
          </div>
        )}

        <div
          style={{
            background: '#0d1117',
            border: '1px solid #21262d',
            boxShadow: '0 0 0 1px #161b22, 0 12px 32px rgba(0, 0, 0, 0.6)',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
